/**
 * Copyright Highway 9 Networks Inc.
 */


import Radio from "~/types/radio";
import { GroupPhaseType } from "~/types/radioGroup";

// Source: https://5g-tools.com/4g-lte-earfcn-calculator/
type BandInfo = {
  uplinkFrequencyStart?: number;
  uplinkFrequencyLast?: number;
  uplinkEarfcnStart?: number;
  uplinkEarfcnEnd?: number;

  downlinkFrequencyStart: number;
  downlinkFrequencyLast: number;
  downlinkEarfcnStart: number;
  downlinkEarfcnEnd: number;

  deltaFGlobal: number;
  duplexMode: "FDD" | "TDD";
};

export const bandData: Record<string, BandInfo> = {
  B1: {
    uplinkFrequencyStart: 1920,
    uplinkFrequencyLast: 1980,
    uplinkEarfcnStart: 18000,
    uplinkEarfcnEnd: 18599,

    downlinkFrequencyStart: 2110,
    downlinkFrequencyLast: 2170,
    downlinkEarfcnStart: 0,
    downlinkEarfcnEnd: 599,

    deltaFGlobal: 0.1,
    duplexMode: "FDD",
  },
  B42: {
    downlinkFrequencyStart: 3400,
    downlinkFrequencyLast: 3600,
    downlinkEarfcnStart: 41590,
    downlinkEarfcnEnd: 43589,

    deltaFGlobal: 0.1,
    duplexMode: "TDD",
  },
  B43: {
    downlinkFrequencyStart: 3600,
    downlinkFrequencyLast: 3800,
    downlinkEarfcnStart: 43590,
    downlinkEarfcnEnd: 45589,

    deltaFGlobal: 0.1,
    duplexMode: "TDD",
  },
  B48: {
    downlinkFrequencyStart: 3550,
    downlinkFrequencyLast: 3700,
    downlinkEarfcnStart: 55240,
    downlinkEarfcnEnd: 56739,

    deltaFGlobal: 0.1,
    duplexMode: "TDD",
  },
  N48: {
    downlinkFrequencyStart: 3550,
    downlinkFrequencyLast: 3700,
    downlinkEarfcnStart: 636667,
    downlinkEarfcnEnd: 646666,

    deltaFGlobal: 0.015,
    duplexMode: "TDD",
  },
  N77: {
    downlinkFrequencyStart: 3300,
    downlinkFrequencyLast: 4200,
    downlinkEarfcnStart: 620000,
    downlinkEarfcnEnd: 680000,

    deltaFGlobal: 0.015,
    duplexMode: "TDD",
  },
  N78: {
    downlinkFrequencyStart: 3300,
    downlinkFrequencyLast: 3800,
    downlinkEarfcnStart: 620000,
    downlinkEarfcnEnd: 653332,

    deltaFGlobal: 0.015,
    duplexMode: "TDD",
  },
};

export function computeFrequency(band?: string | null, earfcn?: number | null) {
  if (earfcn === undefined || earfcn === null) return;
  if (!band || !(band in bandData)) return "";

  const b = bandData[band];
  return Math.round(b.downlinkFrequencyStart + b.deltaFGlobal * (earfcn - b.downlinkEarfcnStart));
}

export const SUBFRAME_ASSIGNMENT: Record<number, string> = {
  1: "TDD Configuration 1 : (DL: UL = 2:2) : (Default, Optimal)",
  2: "TDD Configuration 2 : (DL: UL = 3:1) : (for better downlink performance)",
  6: "TDD Configuration 6 : (DL: UL = 3:5) : (for better uplink performance)",
};

export const SUBFRAME_ASSIGNMENT_5G: Record<number, string> = {
  1: "TDD Configuration 1: (DL: UL = 8:2) : (Default, better downlink performance)",
  // 1: "TDD Configuration 1 : (DL: UL = 7:3) : (for better downlink performance)",
  2: "TDD Configuration 2 : (DL: UL = 6:4) : (for better uplink performance)",
};

export const SUBFRAME_PATTERN: Record<number, string> = {
  5: "5 : Large coverage radius, higher latency",
  7: "7 : Small coverage radius, lower latency",
};

export function getSubframeAssignmentText(value?: number) {
  if (value === undefined || value === null) return;
  return SUBFRAME_ASSIGNMENT[value];
}

export function getSubframeAssignmentText5G(value?: number) {
  if (value === undefined || value === null) return;
  return SUBFRAME_ASSIGNMENT_5G[value];
}

export function getSubframePatternText(value?: number) {
  if (value === undefined || value === null) return;
  return SUBFRAME_PATTERN[value];
}

/**
 * Get the metadata for the life cycle stage.
 */
export const getLifeCycleStageMetadata = ({ state }: { state?: GroupPhaseType }) => {
  let c: { title: string; abbr: string; color: string; textColor: string } | null = null;
  switch (state) {
    case GroupPhaseType.PLANNING:
      c = { title: "Planning", abbr: "P", color: "#eeeeee", textColor: "black" };
      break;
    case GroupPhaseType.SURVEY:
      c = { title: "Survey", abbr: "S", color: "#ffca28", textColor: "black" };
      break;
    case GroupPhaseType.INSTALLATION:
      c = { title: "Installation", abbr: "I", color: "#f57c00", textColor: "white" };
      break;
    case GroupPhaseType.TESTING:
      c = { title: "Testing", abbr: "T", color: "#2196f3", textColor: "white" };
      break;
    case GroupPhaseType.PROVISIONED:
      c = { title: "Provisioned", abbr: "D", color: "#226a58", textColor: "white" };
      break;
    default:
      break;
  }
  return c;
};

/**
 * Get the icon type for the radio.
 * @param data - The radio data.
 * @returns "indoor" | "outdoor"
 */
export function getRadioIconType(data?: Radio | null): "indoor" | "outdoor" {
  if (!data) return "outdoor";
  if (
    data?.model === "Indoor" ||
    data?.sasConfig?.cbsdCategory === "A" ||
    data?.radioGroup?.type === "INDOOR" ||
    data?.sasConfig?.cbsdCategory === undefined
  ) return "indoor";
  if (
    data?.model === "Outdoor" ||
    data?.sasConfig?.cbsdCategory === "B" ||
    data?.radioGroup?.type === "OUTDOOR" ||
    data?.model === "Flex"
  ) return "outdoor";
  return "outdoor";
}